export interface DetectedFileType {
  ext: string;
  mime: string;
}

function toBytes(signature: number[] | string): number[] {
  return typeof signature === 'string' ? Array.from(signature, (c) => c.charCodeAt(0)) : signature;
}

function matchesAt(bytes: Uint8Array, signature: number[] | string, offset = 0): boolean {
  return toBytes(signature).every((byte, i) => bytes[offset + i] === byte);
}

function indexOfIn(bytes: Uint8Array, signature: number[] | string, start: number, end: number): number {
  const sig = toBytes(signature);
  for (let i = start; i + sig.length <= end; i++) {
    if (sig.every((byte, j) => bytes[i + j] === byte)) {
      return i;
    }
  }
  return -1;
}

// Only ever compared with ASCII, so Latin-1 decoding gives the same answers as UTF-8.
function latin1(bytes: Uint8Array, start: number, end: number): string {
  let text = '';
  for (let i = start; i < end; i++) {
    text += String.fromCharCode(bytes[i]);
  }
  return text;
}

function readUInt16LE(bytes: Uint8Array, offset: number): number {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function readUInt32LE(bytes: Uint8Array, offset: number): number {
  return readUInt16LE(bytes, offset) + readUInt16LE(bytes, offset + 2) * 0x10000;
}

function readInt32BE(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3];
}

// Walks zip local file headers the way file-type 16 does, including its quirks.
function detectZipContainer(bytes: Uint8Array): DetectedFileType {
  const size = bytes.length;
  const officeTypes: Record<string, DetectedFileType> = {
    word: { ext: 'docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
    ppt: { ext: 'pptx', mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' },
    xl: { ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
  };
  const openDocumentExtensions: Record<string, string> = {
    'application/vnd.oasis.opendocument.text': 'odt',
    'application/vnd.oasis.opendocument.spreadsheet': 'ods',
    'application/vnd.oasis.opendocument.presentation': 'odp'
  };
  let position = 0;

  while (position + 30 < size) {
    const compressedSize = readUInt32LE(bytes, position + 18);
    const uncompressedSize = readUInt32LE(bytes, position + 22);
    const filenameLength = readUInt16LE(bytes, position + 26);
    const extraFieldLength = readUInt16LE(bytes, position + 28);
    position += 30;

    if (position + filenameLength > size) {
      break;
    }
    const filename = latin1(bytes, position, position + filenameLength);
    position = Math.min(position + filenameLength + extraFieldLength, size);

    if (filename.endsWith('.rels') || filename.endsWith('.xml')) {
      const officeType = officeTypes[filename.split('/')[0]];
      if (officeType) {
        return officeType;
      }
    }

    if (filename.startsWith('xl/')) {
      return officeTypes.xl;
    }

    if (filename === 'mimetype' && compressedSize === uncompressedSize) {
      if (position + compressedSize > size) {
        break;
      }
      const mimeType = latin1(bytes, position, position + compressedSize);
      position += compressedSize;
      if (openDocumentExtensions[mimeType]) {
        return { ext: openDocumentExtensions[mimeType], mime: mimeType };
      }
    }

    if (compressedSize === 0) {
      // Sizes live in a trailing data descriptor; scan ahead in 4100-byte windows for the next header.
      let nextHeaderIndex = -1;
      while (nextHeaderIndex < 0 && position < size) {
        const windowEnd = Math.min(position + 4100, size);
        const found = indexOfIn(bytes, 'PK\x03\x04', position, windowEnd);
        nextHeaderIndex = found >= 0 ? found - position : -1;
        position = Math.min(position + (found >= 0 ? nextHeaderIndex : 4100), size);
      }
    } else {
      // Skips the mimetype payload a second time, as file-type does.
      position = Math.min(position + compressedSize, size);
    }
  }

  return { ext: 'zip', mime: 'application/zip' };
}

function detectPng(bytes: Uint8Array): DetectedFileType | undefined {
  let position = 8;
  do {
    if (position + 8 > bytes.length) {
      return undefined;
    }
    const length = readInt32BE(bytes, position);
    const type = latin1(bytes, position + 4, position + 8);
    position += 8;

    if (length < 0) {
      return undefined;
    }
    if (type === 'IDAT') {
      return { ext: 'png', mime: 'image/png' };
    }
    if (type === 'acTL') {
      return { ext: 'apng', mime: 'image/apng' };
    }
    position = Math.min(position + length + 4, bytes.length);
  } while (position + 8 < bytes.length);

  return { ext: 'png', mime: 'image/png' };
}

/**
 * Identifies the upload types the app accepts from their magic bytes, returning what file-type 16
 * FileType.fromBuffer did for them. Anything else is undefined.
 */
export function detectFileType(input: ArrayBuffer | Uint8Array): DetectedFileType | undefined {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.length <= 1) {
    return undefined;
  }

  // file-type reads the first 12 bytes into a zero-filled buffer for its initial checks.
  const head = new Uint8Array(12);
  head.set(bytes.subarray(0, 12));

  if (matchesAt(head, 'BM')) {
    return { ext: 'bmp', mime: 'image/bmp' };
  }
  if (matchesAt(head, [0xff, 0xd8, 0xff])) {
    return { ext: 'jpg', mime: 'image/jpeg' };
  }
  if (matchesAt(head, 'GIF')) {
    return { ext: 'gif', mime: 'image/gif' };
  }
  if (matchesAt(head, 'WEBP', 8)) {
    return { ext: 'webp', mime: 'image/webp' };
  }
  if (matchesAt(head, 'PK\x03\x04')) {
    return detectZipContainer(bytes);
  }
  if (matchesAt(head, 'ftyp', 4) && (head[8] & 0x60) !== 0) {
    const brand = latin1(head, 8, 12).replace('\0', ' ').trim();
    switch (brand) {
      case 'avif':
        return { ext: 'avif', mime: 'image/avif' };
      case 'mif1':
        return { ext: 'heic', mime: 'image/heif' };
      case 'msf1':
        return { ext: 'heic', mime: 'image/heif-sequence' };
      case 'heic':
      case 'heix':
        return { ext: 'heic', mime: 'image/heic' };
      case 'hevc':
      case 'hevx':
        return { ext: 'heic', mime: 'image/heic-sequence' };
      default:
        return undefined;
    }
  }
  if (matchesAt(head, '%PDF')) {
    const start = Math.min(1350, bytes.length);
    const end = Math.min(start + 10 * 1024 * 1024, bytes.length);
    return indexOfIn(bytes, 'AIPrivateData', start, end) >= 0
      ? { ext: 'ai', mime: 'application/postscript' }
      : { ext: 'pdf', mime: 'application/pdf' };
  }
  if (matchesAt(head, 'II*\0') || matchesAt(head, 'MM\0*')) {
    return { ext: 'tif', mime: 'image/tiff' };
  }
  if (matchesAt(head, '{\\rtf')) {
    return { ext: 'rtf', mime: 'application/rtf' };
  }
  if (matchesAt(head, '<?xml ')) {
    return { ext: 'xml', mime: 'application/xml' };
  }
  if (matchesAt(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return detectPng(bytes);
  }
  if (matchesAt(head, [0x00, 0x00, 0x01, 0x00])) {
    return { ext: 'ico', mime: 'image/x-icon' };
  }
  if (matchesAt(head, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
    return { ext: 'cfb', mime: 'application/x-cfb' };
  }

  return undefined;
}
